import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

/**
 * WaveformBars — Real or decorative waveform bar visualisation.
 *
 * When `audioUrl` is provided, decodes the audio file and renders bars
 * representing actual amplitude data with played/unplayed coloring.
 * When no `audioUrl`, falls back to decorative bell-curve bars.
 *
 * @example
 * <!-- Real waveform with progress and seek -->
 * <om-waveform-bars
 *   [audioUrl]="url"
 *   [progress]="0.4"
 *   [isAnimating]="isPlaying()"
 *   [autoFill]="true"
 *   [heightPx]="28"
 *   (seeked)="onSeek($event)"
 * />
 *
 * @example
 * <!-- Decorative static bars -->
 * <om-waveform-bars [heightPx]="24" [barCount]="30" />
 */
@Component({
  selector: 'om-waveform-bars',
  imports: [],
  templateUrl: './waveform-bars.html',
  styleUrl: './waveform-bars.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WaveformBars {
  private readonly el = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  /** URL of the audio file to decode for real waveform. Null = decorative mode. */
  audioUrl = input<string | null>(null);
  /** Playback progress 0–1. Controls played/unplayed bar coloring. */
  progress = input<number>(0);
  /** When true, played bars pulse subtly. */
  isAnimating = input<boolean>(false);
  /** Container height in pixels. */
  heightPx = input<number>(24);
  /** Number of bars (ignored when autoFill is true). */
  barCount = input<number>(20);
  /** Dynamically compute bar count from container width. */
  autoFill = input<boolean>(false);

  /** Emits 0–1 position when user clicks the waveform to seek. */
  seekPosition = output<number>();

  private readonly autoBarCount = signal(20);
  private readonly rawChannelData = signal<Float32Array | null>(null);
  protected readonly isDecoding = signal(false);
  private decodeGeneration = 0;

  constructor() {
    afterNextRender(() => {
      if (!this.autoFill()) return;

      const host = this.el.nativeElement as HTMLElement;
      const ro = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? 0;
        const count = Math.max(4, Math.floor(width / 5));
        this.autoBarCount.set(count);
      });
      ro.observe(host);
      this.destroyRef.onDestroy(() => ro.disconnect());
    });

    // Decode audio when audioUrl changes
    effect(() => {
      const url = this.audioUrl();
      if (!url) {
        this.rawChannelData.set(null);
        this.isDecoding.set(false);
        return;
      }
      this.isDecoding.set(true);
      void this.decodeAudio(url);
    });
  }

  private readonly effectiveBarCount = computed(() =>
    this.autoFill() ? this.autoBarCount() : this.barCount()
  );

  /** Sampled peaks from decoded audio, or null for decorative mode. */
  private readonly peaks = computed(() => {
    const data = this.rawChannelData();
    const count = this.effectiveBarCount();
    if (!data) return null;
    return this.samplePeaks(data, count);
  });

  protected readonly hasRealWaveform = computed(() => this.peaks() !== null);

  /** Final bar data for the template. */
  protected readonly bars = computed(() => {
    const peaks = this.peaks();
    const count = this.effectiveBarCount();
    const prog = this.progress();
    const height = this.heightPx();

    if (peaks) {
      return peaks.map((amplitude, i) => {
        const minH = 3;
        const h = Math.round(minH + amplitude * (height - minH));
        const played = i / count <= prog;
        return { id: i, height: `${h}px`, delay: `${(i * 70) % 900}ms`, played };
      });
    }

    // Decorative fallback
    return Array.from({ length: count }, (_, i) => {
      const t = i / (count - 1 || 1);
      const bell = Math.sin(t * Math.PI);
      const jitter = Math.abs(Math.sin(i * 2.3 + 1)) * 6;
      const h = Math.round(4 + bell * 14 + jitter);
      return { id: i, height: `${h}px`, delay: `${(i * 70) % 900}ms`, played: false };
    });
  });

  protected onWaveformClick(event: MouseEvent | TouchEvent | Event): void {
    if (!this.hasRealWaveform()) return;

    let clientX: number;
    if (event instanceof TouchEvent) {
      const touch = event.changedTouches[0];
      if (!touch) return;
      clientX = touch.clientX;
    } else if (event instanceof MouseEvent) {
      clientX = event.clientX;
    } else {
      return;
    }

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    this.seekPosition.emit(ratio);
  }

  private async decodeAudio(url: string): Promise<void> {
    const gen = ++this.decodeGeneration;

    try {
      const response = await fetch(url);
      if (gen !== this.decodeGeneration) return;

      const arrayBuffer = await response.arrayBuffer();
      if (gen !== this.decodeGeneration) return;

      // OfflineAudioContext decodes without activating the audio hardware
      // session on iOS — avoids conflicts with the audio playback element.
      const ctx = new OfflineAudioContext(1, 1, 44100);
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

      if (gen !== this.decodeGeneration) return;

      this.rawChannelData.set(audioBuffer.getChannelData(0));
      this.isDecoding.set(false);
    } catch {
      if (gen === this.decodeGeneration) {
        this.rawChannelData.set(null);
        this.isDecoding.set(false);
      }
    }
  }

  private samplePeaks(data: Float32Array, barCount: number): number[] {
    const samplesPerBar = Math.floor(data.length / barCount);
    if (samplesPerBar < 1) return Array(barCount).fill(0.5);

    const raw: number[] = [];
    let max = 0;

    for (let i = 0; i < barCount; i++) {
      const start = i * samplesPerBar;
      let sum = 0;
      for (let j = start; j < start + samplesPerBar; j++) {
        sum += Math.abs(data[j]);
      }
      const avg = sum / samplesPerBar;
      raw.push(avg);
      if (avg > max) max = avg;
    }

    if (max === 0) return raw.map(() => 0.3);

    // Normalize and apply 3-point moving average for smoothing
    const normalized = raw.map(v => v / max);
    const smoothed: number[] = [];
    for (let i = 0; i < normalized.length; i++) {
      const prev = normalized[i - 1] ?? normalized[i];
      const next = normalized[i + 1] ?? normalized[i];
      smoothed.push((prev + normalized[i] + next) / 3);
    }

    return smoothed;
  }
}
